import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useInteractiveFlag } from '@/hooks/useInteractiveFlag';
import { logout } from '@/actions/authActions';
import { setSelectedVersion } from '@/actions/common/commonPure';
import { refreshAccessToken } from '@/config/axios';
import { hasLiveRefreshToken } from '@/config/session';
import { API_KEY } from '@/config/constants';
import RemoteErrorBoundary from './RemoteErrorBoundary';

export function InteractivePage() {
  const { enabled, loading } = useInteractiveFlag();
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const selectedProcess = useSelector(
    (state: any) => state.core.selected_process,
  );
  const processId = selectedProcess?.optimization_id
    ? String(selectedProcess.optimization_id)
    : null;

  const selectedVersion = useSelector((state: any) => state.core.selected_version);
  const versionId = selectedVersion != null ? String(selectedVersion) : null;

  const token = useSelector((state: any) => state.login.token) || '';
  const refreshToken = sessionStorage.getItem('refresh') || '';
  const apiBaseUrl = (window as any).instance?.customerurl || '';
  const locale = localStorage.getItem('system_language') || 'de';
  // The MFE's map runs on HERE too, but it is a separate bundle and cannot import ours.
  // Passing the key here keeps ONE key in the repo and lets it change without an MFE build.
  const hereApiKey = API_KEY;

  // Mount / re-mount MFE when context changes.
  //
  // `token` and `refreshToken` are intentionally excluded from the dep array:
  // the MFE listens for the `mfe:token-refreshed` event and updates its
  // internal token without remounting, so rotating the access token never
  // tears down the MFE's React tree, router, or React Query cache.
  useEffect(() => {
    const container = containerRef.current;
    if (enabled && container) {
      import('interactiveApp/mount')
        .then((m) => {
          if (m.mount) {
            m.mount(container, {
              token,
              refreshToken,
              processId,
              versionId,
              apiBaseUrl,
              locale,
              hereApiKey,
            });
          }
        })
        .catch(console.error);

      return () => {
        import('interactiveApp/mount')
          .then((m) => {
            if (m.unmount) m.unmount();
          })
          .catch(() => { });
      };
    }
    // biome-ignore lint/correctness/useExhaustiveDependencies: token/refreshToken intentionally excluded — see comment above
  }, [enabled, processId, versionId, apiBaseUrl, locale, hereApiKey]);

  // Listen for auth-expired events from the MFE
  useEffect(() => {
    // The MFE reports any failed refresh, including a network error. Only log out
    // when the refresh token is actually gone or expired.
    const handleAuthExpired = () => {
      if (!hasLiveRefreshToken()) (dispatch as any)(logout());
    };
    const handleRefreshRequest = async () => {
      try {
        // On success `refreshAccessToken` (performRefresh) broadcasts
        // `mfe:token-refreshed` itself, so we only need to surface failures.
        await refreshAccessToken();
      } catch {
        window.dispatchEvent(new CustomEvent('mfe:refresh-failed'));
      }
    };
    window.addEventListener('mfe:auth-expired', handleAuthExpired);
    window.addEventListener('mfe:refresh-request', handleRefreshRequest);
    return () => {
      window.removeEventListener('mfe:auth-expired', handleAuthExpired);
      window.removeEventListener('mfe:refresh-request', handleRefreshRequest);
    };
  }, [dispatch]);

  // The MFE saved a new version → pin the selector to it. This re-mounts the MFE
  // with the new versionId and makes PlanningActions refetch its version list.
  useEffect(() => {
    const handleVersionCreated = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};
      if (detail.optimizationId && detail.optimizationId !== processId) return;
      (dispatch as any)(setSelectedVersion(detail.versionId ?? null));
    };
    window.addEventListener('mfe:version-created', handleVersionCreated);
    return () => window.removeEventListener('mfe:version-created', handleVersionCreated);
  }, [dispatch, processId]);

  // While flag is resolving, render nothing to avoid flash
  if (loading) return null;

  // Flag false / request failed → redirect to parent (which routes to dashboard)
  if (!enabled) return <Navigate to=".." replace />;

  return (
    <RemoteErrorBoundary>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
    </RemoteErrorBoundary>
  );
}
